import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { usePermissions } from '../../hooks/usePermissions'
import { 
  Home, Users, Heart, Calendar, FileText, Settings, BarChart3, Building2, User, Globe, Shield, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Activity, Bell, CreditCard, QrCode, TrendingUp
} from 'lucide-react'
import { ArtemisMascotMark, ArtemisWordmark } from '../brand'

// Navegación completa (para ADMIN, SUPERADMIN, VETERINARIO)
const fullNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Acceso QR', href: '/acceso-mascotas', icon: QrCode },
  { name: 'Veterinarios', href: '/veterinarios', icon: User },
  { name: 'Clínicas', href: '/clinicas', icon: Building2 },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
]

// Navegación para SUPERADMIN (incluye Configuración y Usuarios)
const superAdminNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Acceso QR', href: '/acceso-mascotas', icon: QrCode },
  { name: 'Usuarios', href: '/usuarios', icon: Users },
  { name: 'Veterinarios', href: '/veterinarios', icon: User },
  { name: 'Clínicas', href: '/clinicas', icon: Building2 },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
  {
    name: 'Configuración',
    icon: Settings,
    children: [
      { name: 'Roles y Usuarios', href: '/configuracion/roles', icon: Shield },
      { name: 'Condiciones de Salud', href: '/configuracion/condiciones-salud', icon: Activity },
      { name: 'Push condición salud', href: '/configuracion/push-condicion-salud', icon: Bell },
      { name: 'SEO', href: '/configuracion/seo', icon: TrendingUp },
      { name: 'Países', href: '/configuracion/paises', icon: Globe },
      { name: 'Sistema', href: '/configuracion/sistema', icon: Settings },
    ]
  },
]

// Navegación restringida para DUEÑO (sin Reportes: backend solo permite ADMIN/SUPERADMIN/VETERINARIO)
const duenoNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Mis Suscripciones', href: '/suscripciones-clinicas', icon: CreditCard },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
]

// Navegación restringida para PET_SITTER (sin Reportes: mismo criterio backend)
const petSitterNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
]

// Navegación para VETERINARIO admin de clínica
const veterinarioAdminNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Acceso QR', href: '/acceso-mascotas', icon: QrCode },
  { name: 'Veterinarios', href: '/veterinarios', icon: User },
  { name: 'Clínicas', href: '/clinicas', icon: Building2 },
  { name: 'Suscripciones Pendientes', href: '/suscripciones-pendientes', icon: CreditCard },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell },
]

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation()
  const { role, isVeterinarioAdmin } = usePermissions()
  
  // Determinar navegación según permisos (alineado con permissions.js)
  const navigation = useMemo(() => {
    if (role === 'DUEÑO') return duenoNavigation
    if (role === 'PET_SITTER') return petSitterNavigation
    if (role === 'SUPERADMIN') return superAdminNavigation
    if (role === 'VETERINARIO') {
      if (isVeterinarioAdmin) return veterinarioAdminNavigation
      return fullNavigation.filter(item => item.name !== 'Veterinarios')
    }
    return fullNavigation
  }, [role, isVeterinarioAdmin])
  
  // Abrir automáticamente el submenu si estamos en una de sus rutas
  const getInitialOpenSubmenus = () => {
    const open = new Set()
    if (location.pathname.startsWith('/configuracion')) {
      open.add('Configuración')
    }
    return open
  }
  
  const [openSubmenus, setOpenSubmenus] = useState(getInitialOpenSubmenus())

  useEffect(() => {
    if (location.pathname.startsWith('/configuracion')) {
      setOpenSubmenus(prev => new Set([...prev, 'Configuración']))
    }
  }, [location.pathname])

  const toggleSubmenu = (itemName) => {
    setOpenSubmenus(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemName)) {
        newSet.delete(itemName)
      } else {
        newSet.add(itemName)
      }
      return newSet
    })
  }

  const isItemActive = (item) => {
    if (item.href) {
      return location.pathname === item.href
    }
    if (item.children) {
      return item.children.some(child => location.pathname === child.href)
    }
    return false
  }

  return (
    <div className={`flex flex-col bg-white/95 dark:bg-slate-700 border-r border-pastel-pink dark:border-slate-600 transition-all duration-300 ease-in-out ${collapsed ? 'w-16' : 'w-64'} h-full relative overflow-hidden backdrop-blur-sm`}>
      <div className="absolute inset-0 bg-gradient-to-b from-pastel-cream/60 via-white/40 to-transparent dark:from-slate-600/50 pointer-events-none" />
      <div className="flex items-center justify-between gap-2 p-3 sm:p-4 border-b border-pastel-pink dark:border-slate-600 relative z-10">
        <div
          className={`flex items-center min-w-0 flex-1 ${collapsed ? 'justify-center' : ''}`}
        >
          {!collapsed ? (
            <ArtemisWordmark mascotSize={36} className="animate-fade-in" />
          ) : (
            <ArtemisMascotMark size={28} className="rounded-[8px] flex-shrink-0" />
          )}
        </div>
        <button
          onClick={onToggle}
          className="p-2 rounded-xl hover:bg-pastel-cream dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 transition-all duration-300 flex-shrink-0 hover:scale-105 active:scale-95"
          title={collapsed ? 'Expandir' : 'Colapsar'}
          aria-label={collapsed ? 'Expandir' : 'Colapsar'}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5 transition-transform duration-300" />
          ) : (
            <ChevronLeft className="w-5 h-5 transition-transform duration-300" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 sm:py-4 space-y-1 overflow-y-auto relative z-10">
        {navigation.map((item, index) => {
          const isActive = isItemActive(item)
          const hasChildren = item.children && item.children.length > 0
          const isSubmenuOpen = hasChildren && openSubmenus.has(item.name)

          return (
            <div key={item.name} className="animate-fade-in-up" style={{ animationDelay: `${index * 50}ms` }}>
              {hasChildren ? (
                <div>
                  <button
                    onClick={() => toggleSubmenu(item.name)}
                    className={`group w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 relative overflow-hidden ${
                      isActive
                        ? 'bg-gradient-to-r from-pastel-teal/15 to-primary-100/50 dark:from-teal-900/30 dark:to-teal-800/20 text-pastel-teal dark:text-teal-400 border-r-2 border-pastel-teal shadow-soft'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-pastel-cream dark:hover:bg-slate-600 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {/* Efecto de brillo en hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-400/0 to-teal-500/0 group-hover:from-teal-400/10 group-hover:to-teal-500/10 transition-all duration-300" />
                    <div className="flex items-center space-x-3 relative z-10">
                      <item.icon className={`flex-shrink-0 w-5 h-5 transition-all duration-300 ${isActive ? 'text-teal-600 dark:text-teal-400' : 'text-gray-500 dark:text-slate-400 group-hover:text-gray-700 dark:group-hover:text-slate-300'}`} />
                      {!collapsed && <span className="truncate">{item.name}</span>}
                    </div>
                    {!collapsed && (
                      <div className="flex-shrink-0 relative z-10 transition-transform duration-300 group-hover:scale-110">
                        {isSubmenuOpen ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                    )}
                  </button>
                  
                  {!collapsed && isSubmenuOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 dark:border-slate-500 pl-4">
                      {item.children.map((child) => {
                        const isChildActive = location.pathname === child.href
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={`group flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                              isChildActive
                                ? 'bg-pastel-cream text-pastel-teal dark:bg-teal-900/20 dark:text-teal-400'
                                : 'text-gray-700 dark:text-slate-400 hover:bg-pastel-cream dark:hover:bg-slate-600 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          >
                            <child.icon className={`flex-shrink-0 w-4 h-4 mr-3 ${isChildActive ? 'text-teal-600 dark:text-teal-400' : 'text-gray-500 dark:text-slate-400 group-hover:text-gray-700 dark:group-hover:text-slate-300'}`} />
                            <span className="truncate">{child.name}</span>
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.href}
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 relative overflow-hidden ${
                    isActive
                      ? 'bg-gradient-to-r from-pastel-teal/15 to-primary-100/50 dark:from-teal-900/30 dark:to-teal-800/20 text-pastel-teal dark:text-teal-400 border-r-2 border-pastel-teal shadow-sm'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-pastel-cream dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white hover:shadow-sm'
                  }`}
                  title={collapsed ? item.name : ''}
                >
                  {/* Efecto de brillo en hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-400/0 to-teal-500/0 group-hover:from-teal-400/10 group-hover:to-teal-500/10 transition-all duration-300" />
                  <item.icon className={`flex-shrink-0 w-5 h-5 transition-all duration-300 relative z-10 ${collapsed ? 'mx-auto' : 'mr-3'} ${isActive ? 'text-teal-600 dark:text-teal-400' : 'text-gray-500 dark:text-slate-400 group-hover:text-gray-700 dark:group-hover:text-slate-300'}`} />
                  {!collapsed && <span className="truncate relative z-10">{item.name}</span>}
                </Link>
              )}
            </div>
          )
        })}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-gray-200 dark:border-slate-600">
          <div className="text-xs text-gray-500 dark:text-slate-400 text-center">
            Artemis v1.0
          </div>
        </div>
      )}
    </div>
  )
}
